"use client";

import { Loading } from "@/components/ui/loading";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Application } from "../types";
import ApplicationOrCandidateProfileList from "@/features/common/ApplicationOrCandidateProfileList";
import { useTranslation } from "react-i18next";

interface BaseApplicationListProps {
  applications: Application[];
  loading: boolean;
  error: string | null;
}

export default function BaseApplicationList({
  applications,
  loading,
  error
}: BaseApplicationListProps) {
  const { toast } = useToast();
  const { t } = useTranslation(["applications"]);

  const handleExport = (data: Application[]) => {
    const csvContent = [
      // Headers
      [
        t("columns.name"),
        t("columns.job_title"),
        t("columns.qualification"),
        t("columns.phase"),
        t("columns.status"),
        t("columns.expected_start"),
        t("columns.german_language_skills"),
        t("columns.german_drivers_license"),
        t("columns.nationality"),
        t("columns.gender"),
        t("columns.date_of_birth"),
        t("columns.address"),
        t("columns.postal_code"),
        t("columns.city"),
        t("columns.state"),
        t("columns.country"),
        t("columns.relationship_status"),
        t("columns.criminal_record"),
        t("columns.nursing_education_status"),
        t("columns.recognition_status"),
        t("columns.theory_hours_deficit"),
        t("columns.practice_hours_deficit"),
        t("columns.visa_application_status"),
        t("columns.labor_market_authorization_status"),
        t("columns.health_insurance"),
        t("columns.travel_health_insurance"),
        t("columns.work_experience"),
        t("columns.advanced_training"),
        t("columns.drivers_license"),
        t("columns.facility_preferences"),
        t("columns.location_preferences"),
        t("columns.position_preferences"),
        t("columns.working_hours"),
        t("columns.shift_model"),
        t("columns.earliest_start_date"),
        t("columns.preparation_course_enrolled"),
        t("columns.preparation_course_name"),
        t("columns.preparation_course_provider"),
        t("columns.preparation_course_start_date"),
        t("columns.preparation_course_end_date"),
        t("columns.preparation_exam_date"),
        t("columns.preparation_exam_location"),
        t("columns.preparation_pre_exam"),
        t("columns.created_at"),
        t("columns.updated_at")
      ],
      // Data rows
      ...data.map((app) => [
        `${app.given_name || ""} ${app.family_name || ""}`.trim(),
        app.job_title || t("messages.not_available"),
        app.nursing_education_status === "completed"
          ? t("status_labels.nursing_education.completed")
          : t("status_labels.nursing_education.not_started"),
        "Phase 1", // Simplified for export
        t("status_labels.nursing_education.in_progress"), // Simplified for export
        app.created_at
          ? (() => {
              const date = new Date(app.created_at);
              date.setMonth(date.getMonth() + 5);
              return date.toLocaleDateString("de-DE");
            })()
          : "-",
        app.german_language_skills || "-",
        app.german_drivers_license && app.german_drivers_license.length > 0
          ? app.german_drivers_license.join(", ")
          : "-",
        app.nationality || "-",
        app.gender || "-",
        app.date_of_birth
          ? new Date(app.date_of_birth).toLocaleDateString("de-DE")
          : "-",
        app.address_1 || "-",
        app.postal_code || "-",
        app.city || "-",
        app.state || "-",
        app.country || "-",
        app.relationship_status || "-",
        app.criminal_record || "-",
        app.nursing_education_status || "-",
        app.recognition_status || "-",
        app.theory_hours_deficit || "-",
        app.practice_hours_deficit || "-",
        app.visa_application_status || "-",
        app.labor_market_authorization_status || "-",
        app.health_insurance || "-",
        app.travel_health_insurance || "-",
        app.work_experience || "-",
        app.advanced_training || "-",
        app.drivers_license && app.drivers_license.length > 0
          ? app.drivers_license.join(", ")
          : "-",
        app.facility_preferences && app.facility_preferences.length > 0
          ? app.facility_preferences.join(", ")
          : "-",
        app.location_preferences && app.location_preferences.length > 0
          ? app.location_preferences.join(", ")
          : "-",
        app.position_preferences && app.position_preferences.length > 0
          ? app.position_preferences.join(", ")
          : "-",
        app.working_hours || "-",
        app.shift_model || "-",
        app.earliest_start_date
          ? new Date(app.earliest_start_date).toLocaleDateString("de-DE")
          : "-",
        app.preparation_course_enrolled
          ? t("status_labels.boolean.yes")
          : t("status_labels.boolean.no"),
        app.preparation_course_name || "-",
        app.preparation_course_provider || "-",
        app.preparation_course_start_date
          ? new Date(app.preparation_course_start_date).toLocaleDateString(
              "de-DE"
            )
          : "-",
        app.preparation_course_end_date
          ? new Date(app.preparation_course_end_date).toLocaleDateString(
              "de-DE"
            )
          : "-",
        app.preparation_exam_date
          ? new Date(app.preparation_exam_date).toLocaleDateString("de-DE")
          : "-",
        app.preparation_exam_location || "-",
        app.preparation_pre_exam
          ? t("status_labels.boolean.yes")
          : t("status_labels.boolean.no"),
        app.created_at
          ? new Date(app.created_at).toLocaleDateString("de-DE")
          : "-",
        app.updated_at
          ? new Date(app.updated_at).toLocaleDateString("de-DE")
          : "-"
      ])
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `bewerbungen_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("talent_applications")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: t("messages.success"),
        description: t("messages.application_deleted_successfully")
      });
    } catch (error) {
      console.error("Error deleting application:", error);
      toast({
        title: t("messages.error"),
        description: t("messages.error_deleting_application"),
        variant: "destructive"
      });
    }
  };

  if (loading) return <Loading text={t("messages.loading_applications")} />;

  if (error)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-red-500">
          {t("messages.error")}: {error}
        </div>
      </div>
    );

  return (
    <div>
      <ApplicationOrCandidateProfileList
        applicationsOrProfiles={applications}
        onExport={handleExport}
        onDelete={handleDelete}
        isApplicationList={true}
      />
    </div>
  );
}
